import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { trialsAPI } from '../services/api';
import recentSearchesManager from '../utils/recentSearches';
import RecentSearchesSidebar from '../components/RecentSearchesSidebar';
import SEO from '../components/SEO';

function ClinicalTrials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchType, setSearchType] = useState('general'); // general, condition, intervention
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokens, setPageTokens] = useState([]); // Store page tokens for navigation
  const [nextPageToken, setNextPageToken] = useState(null);
  const resultsPerPage = 10;

  // Auto-search if query parameter is present
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchQuery(query);
      performSearch(query, searchType, 1);
    }
  }, [searchParams, searchType]);

  const performSearch = async (query, type, page = 1, pageToken = null) => {
    setLoading(true);
    setError(null);

    try {
      let response;

      switch (type) {
        case 'general':
          response = await trialsAPI.search(query, resultsPerPage, pageToken);
          break;
        case 'condition':
          response = await trialsAPI.searchByCondition(query, resultsPerPage);
          break;
        case 'intervention':
          response = await trialsAPI.searchByIntervention(query, resultsPerPage);
          break;
        default:
          response = await trialsAPI.search(query, resultsPerPage, pageToken);
      }

      setResults(response.data);
      setCurrentPage(page);

      // Store the next page token from API response
      if (response.data.nextPageToken) {
        setNextPageToken(response.data.nextPageToken);
        // Update page tokens array for navigation
        if (page === 1) {
          setPageTokens([null, response.data.nextPageToken]);
        } else {
          setPageTokens(prev => {
            const newTokens = [...prev];
            newTokens[page] = response.data.nextPageToken;
            return newTokens;
          });
        }
      } else {
        setNextPageToken(null);
      }

      // Save to recent searches (only on first page)
      if (page === 1) {
        recentSearchesManager.addSearch({
          query: query.trim(),
          database: 'trials',
          searchType: type,
          resultsCount: response.data?.studies?.length || 0
        });
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Update URL with search query
    setSearchParams({ q: searchQuery.trim() });

    await performSearch(searchQuery.trim(), searchType, 1);
  };

  const handlePageChange = async (direction) => {
    if (direction === 'next' && nextPageToken) {
      await performSearch(searchQuery.trim(), searchType, currentPage + 1, nextPageToken);
    } else if (direction === 'prev' && currentPage > 1) {
      // For previous page, use stored token
      const prevToken = pageTokens[currentPage - 2] || null;
      await performSearch(searchQuery.trim(), searchType, currentPage - 1, prevToken);
    }
  };

  const renderResults = () => {
    if (!results || !results.studies) return <p className="no-results">No results found</p>;

    return (
      <div className="results-grid">
        {results.studies.map((study, index) => {
          const protocol = study.protocolSection || {};
          const identification = protocol.identificationModule || {};
          const status = protocol.statusModule || {};
          const design = protocol.designModule || {};
          const conditions = protocol.conditionsModule || {};
          const sponsor = protocol.sponsorCollaboratorsModule || {};
          const description = protocol.descriptionModule || {};
          const eligibility = protocol.eligibilityModule || {};
          const interventions = protocol.armsInterventionsModule?.interventions || [];

          return (
            <Link 
              key={index} 
              to={`/trial/${identification.nctId}`}
              className="result-card"
              style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              }}
            >
              <h3 style={{ color: 'var(--secondary-color)' }}>{identification.briefTitle || 'Clinical Trial'}</h3>
              
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Study Information</h4>
                <p><strong>NCT ID:</strong> <a href={`https://clinicaltrials.gov/study/${identification.nctId}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db' }}>{identification.nctId || 'N/A'}</a></p>
                <p><strong>Status:</strong> <span style={{ 
                  color: status.overallStatus === 'RECRUITING' ? '#27ae60' : 
                        status.overallStatus === 'COMPLETED' ? '#3498db' : 
                        status.overallStatus === 'TERMINATED' ? '#e74c3c' : '#7f8c8d',
                  fontWeight: 'bold'
                }}>{status.overallStatus || 'N/A'}</span></p>
                <p><strong>Study Type:</strong> {design.studyType || 'N/A'}</p>
                <p><strong>Phase:</strong> {design.phases?.join(', ') || 'Not Applicable'}</p>
                {identification.officialTitle && (
                  <p><strong>Official Title:</strong> {identification.officialTitle}</p>
                )}
              </div>

              {conditions.conditions && conditions.conditions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Conditions</h4>
                  <p>{conditions.conditions.join(', ')}</p>
                </div>
              )}

              {interventions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Interventions</h4>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {interventions.slice(0, 5).map((intervention, i) => (
                      <li key={i}>
                        <strong>{intervention.type}:</strong> {intervention.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Sponsor & Dates</h4>
                <p><strong>Lead Sponsor:</strong> {sponsor.leadSponsor?.name || 'N/A'}</p>
                {status.startDateStruct && (
                  <p><strong>Start Date:</strong> {status.startDateStruct.date || 'N/A'}</p>
                )}
                {status.completionDateStruct && (
                  <p><strong>Completion Date:</strong> {status.completionDateStruct.date || 'N/A'} ({status.completionDateStruct.type || 'Estimated'})</p>
                )}
              </div>

              {description.briefSummary && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Brief Summary</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{description.briefSummary.substring(0, 400)}{description.briefSummary.length > 400 ? '...' : ''}</p>
                </div>
              )}

              {eligibility && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Eligibility</h4>
                  {eligibility.sex && <p><strong>Sex:</strong> {eligibility.sex}</p>}
                  {eligibility.minimumAge && <p><strong>Age Range:</strong> {eligibility.minimumAge} - {eligibility.maximumAge || 'No Max'}</p>}
                  {eligibility.healthyVolunteers !== undefined && (
                    <p><strong>Healthy Volunteers:</strong> {eligibility.healthyVolunteers ? 'Yes' : 'No'}</p>
                  )}
                </div>
              )}

              {design.enrollmentInfo && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Enrollment</h4>
                  <p><strong>Target:</strong> {design.enrollmentInfo.count || 'N/A'} participants ({design.enrollmentInfo.type || 'Estimated'})</p>
                </div>
              )}

              {/* Source Info and Actions */}
              <div style={{ 
                marginTop: '1.5rem', 
                paddingTop: '1rem', 
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--text-light)' }}>
                  <span style={{ fontSize: '1.2rem' }}>📊</span>
                  <span>Source: <strong style={{ color: 'var(--primary-color)' }}>ClinicalTrials.gov</strong></span>
                </div>
                <div style={{ 
                  background: 'var(--primary-color)',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  View Full Details →
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    'name': 'Clinical Trials Search',
    'description': 'Search clinical trials from ClinicalTrials.gov database',
    'specialty': 'Medical Research',
    'audience': {
      '@type': 'MedicalAudience',
      'audienceType': 'Researchers, Healthcare Professionals, Patients'
    }
  };

  return (
    <div className="page-container">
      <SEO
        title="Clinical Trials Search - ClinicalTrials.gov Database"
        description="Search clinical trials from ClinicalTrials.gov. Find medical research studies by condition, intervention, drug name, or general search. Access comprehensive trial information."
        keywords="clinical trials, medical research, ClinicalTrials.gov, medical studies, drug trials, disease research, medical conditions, interventions"
        canonical="/trials"
        structuredData={seoStructuredData}
      />
      <h1 className="page-title">Clinical Trials Search</h1>
      <p className="page-description">
        Search clinical trials from ClinicalTrials.gov database. Find trials by general search, 
        medical condition, or specific intervention/drug.
      </p>

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="general"
                checked={searchType === 'general'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}General Search
            </label>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="condition"
                checked={searchType === 'condition'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}By Condition
            </label>
            <label>
              <input
                type="radio"
                value="intervention"
                checked={searchType === 'intervention'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}By Intervention/Drug
            </label>
          </div>

          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder={
                searchType === 'condition' 
                  ? 'Enter condition (e.g., cancer, diabetes)...'
                  : searchType === 'intervention'
                  ? 'Enter drug/intervention (e.g., aspirin)...'
                  : 'Enter search term...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <div className="results-header">
            <h2>Results ({results.studies?.length || 0})</h2>
            <div className="pagination-info">
              Page {currentPage}
            </div>
          </div>
          {renderResults()}

          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange('prev')}
              disabled={currentPage === 1 || loading}
              className="pagination-btn"
            >
              ← Previous
            </button>

            <div className="pagination-info">
              Page {currentPage}
            </div>

            <button
              onClick={() => handlePageChange('next')}
              disabled={!nextPageToken || loading}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <RecentSearchesSidebar database="trials" />
    </div>
  );
}

export default ClinicalTrials;
